/**
 * Manifest save의 `space_manifest` 생성 정본 (STUDIO-PACKAGE-003 / ICD-MAIN-226).
 *
 * legacy full save는 전체 ViewRData를 VDB로 싸서 보낸다. manifest save는 변경·추가된
 * Space만 partial VDB에 담고, 최종 Space 구성을 `space_manifest` 배열로 따로 보낸다.
 *
 *  - `{ source: "existing", index: N }` : 기존 `.viewr`의 N번째 Space를 그대로 보존한다.
 *  - `{ source: "vdb", index: K }`      : partial VDB의 K번째 Space를 사용한다.
 *  - 기존 index가 manifest에 없으면 최종 결과에서 삭제된다.
 *  - 배열 순서가 최종 `.viewr`의 Space 순서다.
 *
 * 삭제와 재정렬을 표현하려면 "지금 화면의 Space가 마지막으로 저장된 `.viewr`에서 몇 번째였는지"를
 * 알아야 한다. 기존 dirty ledger는 현재 index 기준 epoch만 들고 있고 저장본 기준 원본 index는
 * 버리므로, 그 매핑을 여기서 별도로 소유한다. 저장 정확성의 정본인 ledger는 건드리지 않는다.
 */

export type SpaceManifestSource = "existing" | "vdb";

export interface SpaceManifestEntry {
  source: SpaceManifestSource;
  index: number;
}

/** 현재 Space가 마지막 저장본에서 차지하던 index. 저장본에 없던 Space는 `null`. */
export type SavedSpaceOrigin = number | null;

export interface SpaceManifestPlan {
  manifest: SpaceManifestEntry[];
  /** partial VDB에 담아야 하는 현재 Space index. manifest의 vdb index 순서와 같다. */
  vdbSpaceIndices: number[];
}

export interface SpaceOriginTracker {
  /** 저장본과 현재 화면이 일치하는 시점(로드 완료·full save 성공)에 원본 매핑을 초기화한다. */
  resetToSaved: (spaceCount: number) => void;
  insertSpace: (spaceIndex: number) => void;
  removeSpace: (spaceIndex: number) => void;
  reorderSpace: (fromIndex: number, toIndex: number) => void;
  getOrigins: () => SavedSpaceOrigin[];
  /** 저장본 기준 매핑을 한 번이라도 확정한 적이 있는지. 없으면 manifest save를 쓰지 않는다. */
  hasBaseline: () => boolean;
  getSavedSpaceCount: () => number;
}

function isSpaceIndex(value: number): boolean {
  return Number.isInteger(value) && value >= 0;
}

export function createSpaceOriginTracker(): SpaceOriginTracker {
  let origins: SavedSpaceOrigin[] = [];
  let savedSpaceCount = 0;
  let baseline = false;

  return {
    resetToSaved(spaceCount) {
      if (!Number.isInteger(spaceCount) || spaceCount < 0) return;
      origins = Array.from({ length: spaceCount }, (_, index) => index);
      savedSpaceCount = spaceCount;
      baseline = true;
    },

    insertSpace(spaceIndex) {
      if (!baseline || !isSpaceIndex(spaceIndex)) return;
      const clamped = Math.min(spaceIndex, origins.length);
      origins.splice(clamped, 0, null);
    },

    removeSpace(spaceIndex) {
      if (!baseline || !isSpaceIndex(spaceIndex)) return;
      if (spaceIndex >= origins.length) return;
      origins.splice(spaceIndex, 1);
    },

    reorderSpace(fromIndex, toIndex) {
      if (!baseline || !isSpaceIndex(fromIndex) || !isSpaceIndex(toIndex)) return;
      if (fromIndex >= origins.length || toIndex >= origins.length) return;
      const [moved] = origins.splice(fromIndex, 1);
      origins.splice(toIndex, 0, moved);
    },

    getOrigins() {
      return [...origins];
    },

    hasBaseline() {
      return baseline;
    },

    getSavedSpaceCount() {
      return savedSpaceCount;
    },
  };
}

/**
 * 현재 Space 구성과 dirty 목록으로 최종 `space_manifest`와 partial VDB 대상 Space를 만든다.
 *
 * 저장본 원본이 없는 Space(새로 추가)와 dirty Space는 VDB에서 오고, 나머지는 기존 `.viewr`를
 * 그대로 보존한다. 저장본에만 있고 현재 화면에 없는 Space는 manifest에서 빠져 삭제된다.
 */
export function buildSpaceManifest({
  origins,
  dirtySpaceIndices,
}: {
  origins: readonly SavedSpaceOrigin[];
  dirtySpaceIndices: readonly number[];
}): SpaceManifestPlan {
  const dirty = new Set(dirtySpaceIndices.filter(isSpaceIndex));
  const manifest: SpaceManifestEntry[] = [];
  const vdbSpaceIndices: number[] = [];

  origins.forEach((origin, spaceIndex) => {
    const mustRepack = origin === null || dirty.has(spaceIndex);
    if (mustRepack) {
      manifest.push({ source: "vdb", index: vdbSpaceIndices.length });
      vdbSpaceIndices.push(spaceIndex);
      return;
    }
    manifest.push({ source: "existing", index: origin });
  });

  return { manifest, vdbSpaceIndices };
}

export interface SpaceManifestValidation {
  valid: boolean;
  reason?: string;
}

/**
 * Main API·Converter가 `VIEWR_UPDATE_MANIFEST_MISMATCH`로 거절할 manifest를 보내기 전에 잡는다.
 * 여기서 막지 못하는 범위 초과·누락은 실제 `.viewr`와 VDB를 읽어야 하므로 Converter가 최종 판정한다.
 */
export function validateSpaceManifest(
  manifest: readonly SpaceManifestEntry[],
  { savedSpaceCount }: { savedSpaceCount: number },
): SpaceManifestValidation {
  const seen = new Set<string>();
  let expectedVdbIndex = 0;

  for (const entry of manifest) {
    if (entry.source !== "existing" && entry.source !== "vdb") {
      return { valid: false, reason: "space_manifest source must be existing or vdb" };
    }
    if (!isSpaceIndex(entry.index)) {
      return { valid: false, reason: "space_manifest index must be a non-negative integer" };
    }

    const key = `${entry.source}:${entry.index}`;
    if (seen.has(key)) {
      return { valid: false, reason: "space_manifest uses the same source and index twice" };
    }
    seen.add(key);

    if (entry.source === "existing" && entry.index >= savedSpaceCount) {
      return { valid: false, reason: "space_manifest existing index is out of range" };
    }
    if (entry.source === "vdb") {
      if (entry.index !== expectedVdbIndex) {
        return { valid: false, reason: "space_manifest vdb indices must be sequential from 0" };
      }
      expectedVdbIndex += 1;
    }
  }

  return { valid: true };
}

export interface ManifestSaveRoutingInput {
  /** rollout flag. 꺼져 있으면 항상 legacy full save를 사용한다. */
  enabled: boolean;
  hasBaseline: boolean;
  savedSpaceCount: number;
  /**
   * 저장 시점 Unity ViewRData의 실제 Space 개수.
   *
   * origin tracker는 editor 구조 변경 event를 미러링해 유지되므로 한 번이라도 놓치면
   * 현실과 어긋난다. 어긋난 매핑으로 manifest를 만들면 보존해야 할 Space를 삭제할 수 있어,
   * 실제 개수와 대조해 다르면 manifest save를 쓰지 않는다.
   */
  currentSpaceCount: number;
  plan: SpaceManifestPlan;
}

export interface ManifestSaveRoutingDecision {
  useManifestSave: boolean;
  reason: string;
}

/**
 * legacy full save와 manifest save 중 어느 Converter 경로로 보낼지 결정한다.
 *
 * 불확실하면 항상 legacy full save로 떨어진다. manifest save는 보존할 Space가 실제로 있을 때만
 * 이득이 있고, 그 외에는 전체를 다시 싸는 편이 더 단순하고 안전하다.
 */
export function decideManifestSaveRouting({
  enabled,
  hasBaseline,
  savedSpaceCount,
  currentSpaceCount,
  plan,
}: ManifestSaveRoutingInput): ManifestSaveRoutingDecision {
  if (!enabled) {
    return { useManifestSave: false, reason: "manifest_save_disabled" };
  }
  if (!hasBaseline) {
    return { useManifestSave: false, reason: "no_saved_space_baseline" };
  }
  // origin 매핑이 실제 Space 구성과 다르면 신뢰할 수 없다. 조용히 잘못 저장하지 않고 물러선다.
  if (plan.manifest.length !== currentSpaceCount) {
    return { useManifestSave: false, reason: "space_origin_out_of_sync" };
  }
  if (plan.manifest.length === 0) {
    // 빈 Project 저장은 Converter가 허용하지만, 전체를 지우는 결과라 명시적 경로로만 다룬다.
    return { useManifestSave: false, reason: "empty_manifest" };
  }

  const validation = validateSpaceManifest(plan.manifest, { savedSpaceCount });
  if (!validation.valid) {
    return { useManifestSave: false, reason: validation.reason ?? "invalid_manifest" };
  }

  const preservedCount = plan.manifest.filter(
    (entry) => entry.source === "existing",
  ).length;
  if (preservedCount === 0) {
    return { useManifestSave: false, reason: "no_preserved_space" };
  }

  // 다시 담을 Space가 하나도 없으면 partial VDB가 Space 0개로 만들어진다. 그 VDB는
  // legacy full save 경로로 잘못 흘러가는 순간 "빈 Project"로 해석되어 모든 Space를
  // 지운다. 재정렬만 있는 저장은 manifest로도 표현할 수 있지만 이득이 작으므로,
  // 가장 위험한 VDB 모양 자체를 만들지 않는 쪽을 택한다.
  if (plan.vdbSpaceIndices.length === 0) {
    return { useManifestSave: false, reason: "no_repacked_space" };
  }

  return { useManifestSave: true, reason: "manifest_save_selected" };
}

const MANIFEST_SAVE_FLAG_ON = new Set(["1", "true", "on", "yes"]);

/**
 * Manifest save rollout flag.
 *
 * AWS DEV(dev2)는 기본 on, 그 밖의 환경은 기본 off다.
 * 명시적인 window/env 설정이 환경 기본값보다 우선하며, off/잘못된 값은
 * DEV에서도 full save를 유지한다. 기존 manifest routing guard는 별도로 적용한다.
 */
export function isManifestSaveEnabled(): boolean {
  const windowFlag =
    typeof window !== "undefined"
      ? (window as { __VIEWR_MANIFEST_SAVE_ENABLED?: unknown })
          .__VIEWR_MANIFEST_SAVE_ENABLED
      : undefined;
  const raw = windowFlag ?? process.env.REACT_APP_VIEWR_MANIFEST_SAVE_ENABLED;
  if (raw == null) return process.env.REACT_APP_DEPLOY_TIER === "dev2";
  if (typeof raw === "boolean") return raw;
  if (typeof raw !== "string") return false;
  return MANIFEST_SAVE_FLAG_ON.has(raw.trim().toLowerCase());
}
